import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Alert, Spinner, Row } from 'react-bootstrap';
import { getAPI, postAPI } from '../../../apis/backend/apiNode';
import html2pdf from 'html2pdf.js';
import { isMobile } from 'react-device-detect';

// ── HtmlBlockEditor ───────────────────────────────────────────
const EDITABLE_TAGS = ['p', 'li', 'td', 'th', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'span'];

const parseBlocks = (html) => {
	if (!html) return [];

	const parser = new DOMParser();
	const doc = parser.parseFromString(html, 'text/html');

	const blocks = [];
	let idx = 0;

	const walk = (node) => {
		if (node.nodeType !== Node.ELEMENT_NODE) return;

		const tag = node.tagName.toLowerCase();

		if (EDITABLE_TAGS.includes(tag)) {
			const text = node.innerHTML.trim();

			if (text) {
				node.setAttribute('data-block-id', idx);

				blocks.push({
					id: idx,
					tag,
					text,
					href: tag === 'a' ? node.getAttribute('href') || '' : '',
				});

				idx++;
			}
		} else {
			node.childNodes.forEach(walk);
		}
	};

	doc.body.childNodes.forEach(walk);

	return {
		blocks,
		stampedHtml: doc.body.innerHTML,
	};
};

const rebuildHtml = (originalHtml, blocks) => {
	let result = originalHtml;
	blocks.forEach((block) => {
		if (block._originalOuterHTML && block._originalOuterHTML !== block.outerHTML) {
			result = result.replace(block._originalOuterHTML, block.outerHTML);
		}
	});
	return result;
};

// ── Styles ────────────────────────────────────────────────────
const styles = {
	editorWrap: {
		borderRadius: 10,
		overflow: 'hidden',
		border: '1px solid #e2e8f0',
		background: '#fff',
	},
	tabBar: {
		display: 'flex',
		borderBottom: '1px solid #e2e8f0',
		background: '#f8fafc',
	},
	tab: (active) => ({
		padding: '9px 18px',
		fontSize: '0.82rem',
		fontWeight: 600,
		cursor: 'pointer',
		border: 'none',
		background: 'none',
		color: active ? '#185FA5' : '#6c757d',
		borderBottom: active ? '2px solid #185FA5' : '2px solid transparent',
		transition: 'all 0.15s',
		display: 'flex',
		alignItems: 'center',
		gap: 6,
	}),
	previewPane: {
		padding: '20px 24px',
		minHeight: 220,
		background: '#fff',
		fontSize: '0.9rem',
		lineHeight: 1.7,
		color: '#1a202c',
	},
	blockRow: (active) => ({
		display: 'flex',
		alignItems: 'flex-start',
		gap: 10,
		padding: '10px 16px',
		borderBottom: '1px solid #f1f5f9',
		background: active ? '#EFF6FF' : '#fff',
		transition: 'background 0.15s',
	}),
	tagBadge: {
		flexShrink: 0,
		marginTop: 3,
		fontSize: '0.6rem',
		fontWeight: 700,
		background: '#e2e8f0',
		color: '#64748b',
		padding: '2px 6px',
		borderRadius: 4,
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
		minWidth: 26,
		textAlign: 'center',
	},
	blockText: {
		flex: 1,
		fontSize: '0.88rem',
		color: '#1e293b',
		lineHeight: 1.65,
		wordBreak: 'break-word',
	},
	editBtn: {
		flexShrink: 0,
		marginTop: 1,
		background: 'none',
		border: '1px solid #e2e8f0',
		borderRadius: 6,
		cursor: 'pointer',
		padding: '3px 10px',
		fontSize: '0.75rem',
		color: '#64748b',
		fontWeight: 500,
		transition: 'all 0.15s',
		whiteSpace: 'nowrap',
	},
	editArea: {
		width: '100%',
		fontSize: '0.88rem',
		padding: '8px 10px',
		border: '1.5px solid #378ADD',
		borderRadius: 6,
		outline: 'none',
		resize: 'vertical',
		fontFamily: 'inherit',
		lineHeight: 1.65,
		color: '#1e293b',
		background: '#f8fbff',
		boxSizing: 'border-box',
	},
	confirmBtn: {
		background: '#185FA5',
		color: '#fff',
		border: 'none',
		borderRadius: 6,
		padding: '4px 14px',
		fontSize: '0.78rem',
		fontWeight: 600,
		cursor: 'pointer',
	},
	cancelBtn: {
		background: 'none',
		color: '#64748b',
		border: '1px solid #e2e8f0',
		borderRadius: 6,
		padding: '4px 12px',
		fontSize: '0.78rem',
		fontWeight: 500,
		cursor: 'pointer',
	},
	savedBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		fontSize: '0.72rem',
		color: '#0F6E56',
		fontWeight: 600,
		background: '#E1F5EE',
		borderRadius: 5,
		padding: '2px 8px',
		marginLeft: 8,
		opacity: 1,
		transition: 'opacity 0.4s',
	},
};
const replaceBlockInHtml = (html, blockId, text, href) => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(html, 'text/html');

	const el = doc.querySelector(`[data-block-id="${blockId}"]`);

	if (!el) return html;

	el.innerHTML = text;

	if (el.tagName.toLowerCase() === 'a') {
		el.setAttribute('href', href || '#');
	}

	return doc.body.innerHTML;
};

const stripBlockIds = (html) => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(html, 'text/html');
	doc.body.querySelectorAll('[data-block-id]').forEach(n => n.removeAttribute('data-block-id'));
	return doc.body.innerHTML;
};
// ── HtmlBlockEditor - Fixed with proper preview refresh ───────────────────
const HtmlBlockEditor = ({ body, onChange }) => {
	const hasHtml = /<[^>]+>/.test(body || '');
	const [tab, setTab] = useState('preview');
	const [blocks, setBlocks] = useState([]);
	const [editingId, setEditingId] = useState(null);
	const [editText, setEditText] = useState('');
	const [savedId, setSavedId] = useState(null);
	// currentHtml is the live working copy — stays in sync with formData.body
	const currentHtmlRef = useRef(body || '');
	const [previewHtml, setPreviewHtml] = useState(body || '');
	const [editHref, setEditHref] = useState('');
	// Re-initialise when parent body changes (e.g. on first load)
	useEffect(() => {
		if (!body) {
			setBlocks([]);
			setPreviewHtml('');
			currentHtmlRef.current = '';
			return;
		}
		const { blocks: parsed, stampedHtml } = parseBlocks(body);
		currentHtmlRef.current = stampedHtml;  // work on stamped copy
		setPreviewHtml(stampedHtml);
		setBlocks(parsed);
	}, [body]);

	const confirmEdit = (block) => {
		const newHtml = replaceBlockInHtml(
			currentHtmlRef.current,
			block.id,
			editText,
			editHref
		);

		currentHtmlRef.current = newHtml;

		const { blocks: newBlocks, stampedHtml } = parseBlocks(newHtml);

		currentHtmlRef.current = stampedHtml;
		setBlocks(newBlocks);
		setPreviewHtml(stampedHtml);

		onChange(stripBlockIds(newHtml));

		setEditingId(null);
		setEditText('');
		setEditHref('');
	};

	const cancelEdit = () => {
		setEditingId(null);
		setEditText('');
		setEditHref('');

	};

	const deleteBlock = (blockId) => {
		const parser = new DOMParser();
		const doc = parser.parseFromString(currentHtmlRef.current, 'text/html');

		const element = doc.querySelector(
			`[data-block-id="${blockId}"]`
		);

		if (!element) return;

		element.remove();

		const newHtml = doc.body.innerHTML;

		currentHtmlRef.current = newHtml;

		const {
			blocks: newBlocks,
			stampedHtml,
		} = parseBlocks(newHtml);

		currentHtmlRef.current = stampedHtml;

		setBlocks(newBlocks);
		setPreviewHtml(stampedHtml);

		onChange(stripBlockIds(stampedHtml));
	};

	if (!hasHtml) {
		return (
			<textarea
				value={body || ''}
				onChange={(e) => onChange(e.target.value)}
				className='form-control'
				rows={10}
			/>
		);
	}

	return (
		<div style={styles.editorWrap}>
			<div style={styles.tabBar}>
				<button type='button' style={styles.tab(tab === 'preview')} onClick={() => setTab('preview')}>
					<svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' aria-hidden='true'>
						<path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' /><circle cx='12' cy='12' r='3' />
					</svg>
					Aperçu
				</button>
				<button type='button' style={styles.tab(tab === 'edit')} onClick={() => setTab('edit')}>
					<svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' aria-hidden='true'>
						<path d='M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' /><path d='M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' />
					</svg>
					Modifier
					{blocks.length > 0 && (
						<span style={{ background: '#E6F1FB', color: '#185FA5', borderRadius: 10, fontSize: '0.65rem', fontWeight: 700, padding: '1px 6px' }}>
							{blocks.length}
						</span>
					)}
				</button>
			</div>

			{tab === 'preview' && (
				<div
					style={styles.previewPane}
					dangerouslySetInnerHTML={{ __html: previewHtml }}
				/>
			)}

			{tab === 'edit' && (
				<div>
					{blocks.length === 0 ? (
						<div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
							Aucun bloc détecté
						</div>
					) : (
						blocks.map((block) => (
							<div key={block.id} style={styles.blockRow(editingId === block.id)}>
								<span style={styles.tagBadge}>{block.tag}</span>

								<div style={{ flex: 1 }}>
									{editingId === block.id ? (
										<>
											<textarea
												autoFocus
												value={editText}
												onChange={(e) => setEditText(e.target.value)}
												rows={Math.max(2, Math.ceil(editText.length / 70))}
												style={styles.editArea}
											/>
											{block.tag === 'a' && (
												<input
													type='text'
													value={editHref}
													onChange={(e) => setEditHref(e.target.value)}
													placeholder='URL du lien'
													className='form-control mt-2'
												/>
											)}
											<div style={{ display: 'flex', gap: 6, marginTop: 7, alignItems: 'center' }}>
												<button type='button' style={styles.confirmBtn} onClick={() => confirmEdit(block)}>
													✓ Confirmer
												</button>
												<button type='button' style={styles.cancelBtn} onClick={cancelEdit}>
													Annuler
												</button>
											</div>
										</>
									) : (
										<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
											<span style={styles.blockText} >{block.text}</span>
											{savedId === block.id && (
												<span style={styles.savedBadge}>✓ Modifié</span>
											)}
										</div>
									)}
								</div>

								{editingId !== block.id && (
									<div style={{ display: 'flex', gap: 6 }}>
										<button
											type='button'
											style={styles.editBtn}
											onClick={() => {
												setEditingId(block.id);
												setEditText(block.text);
												setEditHref(block.href || '');
											}}
											onMouseEnter={(e) => {
												e.currentTarget.style.background = '#f1f5f9';
												e.currentTarget.style.borderColor = '#94a3b8';
											}}
											onMouseLeave={(e) => {
												e.currentTarget.style.background = 'none';
												e.currentTarget.style.borderColor = '#e2e8f0';
											}}
										>
											✏ Modifier
										</button>

										<button
											type='button'
											style={{
												...styles.editBtn,
												color: '#dc2626',
												borderColor: '#fecaca',
											}}
											onClick={() => deleteBlock(block.id)}
										>
											🗑 Supprimer
										</button>
									</div>
								)}
							</div>
						))
					)}
				</div>
			)}
		</div>
	);
};

// ─────────────────────────────────────────────────────────────

const EmailSendPopup = ({
	isOpen,
	setIsOpen,
	model,
	recordId,
	recordData,
	fnSuccess = () => { },
	EmailType,
}) => {
	const [emailData, setEmailData] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
	const [isSending, setIsSending] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');

	const [formData, setFormData] = useState({
		to: '',
		subject: '',
		body: '',
		cc: [],
		bcc: [],
		attachments: [],
	});

	const convertHTMLToPDF = async (htmlContent, filename) => {
		const container = document.createElement('div');
		container.innerHTML = htmlContent;
		container.style.position = 'fixed';
		container.style.left = '-9999px';
		document.body.appendChild(container);
		const pdfBlob = await html2pdf()
			.from(htmlContent)
			.set({
				filename,
				html2canvas: { scale: 2, useCORS: true },
				jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
			})
			.outputPdf('blob');
		document.body.removeChild(container);
		return new File([pdfBlob], filename, { type: 'application/pdf' });
	};

	const downloadAndConvertToFile = async (url, filename, type) => {
		try {
			const fullUrl = `${window.location.origin}/coreService${url}`;
			const response = await fetch(fullUrl);
			if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
			const contentType = response.headers.get('content-type');
			const blob = await response.blob();
			const isPDF = blob.type.includes('pdf') || contentType.includes('pdf');
			if (isPDF) return new File([blob], filename, { type: 'application/pdf' });
			return new File([blob], filename, { type: blob.type || 'application/octet-stream' });
		} catch (error) {
			console.error(`Failed to download ${filename}:`, error);
			throw error;
		}
	};

	const initializeEmailForm = async (response) => {
		try {
			setIsLoadingAttachments(true);
			const attachmentsWithFiles = await Promise.all(
				response?.data?.selectedAttachments?.map(async (attachment) => {
					try {
						return {
							name: attachment?.name,
							url: `${attachment?.url}`,
							originalType: attachment.originalType,
						};
					} catch (error) {
						return {
							...attachment,
							url: `${window.location.origin}${attachment.url}`,
							conversionFailed: true,
							error: error.message,
						};
					}
				}) || [],
			);
			const successfulAttachments = attachmentsWithFiles?.filter((att) => !att.conversionFailed);
			if (successfulAttachments.length !== attachmentsWithFiles.length) {
				const failedCount = attachmentsWithFiles.length - successfulAttachments.length;
				if (failedCount > 0) setError('Impossible de charger les pièces jointes.');
			}
			setFormData((prev) => ({ ...prev, attachments: successfulAttachments }));
			setIsLoadingAttachments(false);
		} catch (error) {
			console.error('Error initializing email form:', error);
			setFormData((prev) => ({
				...prev,
				attachments: response.data.selectedAttachments?.map((i) => ({
					...i,
					url: `${window.location.origin}${i.url}`,
				})) || [],
			}));
			setIsLoadingAttachments(false);
		}
	};
	useEffect(() => {
		if (isOpen && model && recordId) fetchEmailPreview();
	}, [isOpen, model, recordId]);

	const fetchEmailPreview = async () => {
		try {
			setIsLoading(true);
			setError('');
			const response = await getAPI('email', `/preview/${model}/${recordId}?type=${EmailType}`);
			if (response.success) {
				setEmailData(response.data);
				setFormData({
					to: response.data.to,
					subject: response.data.subject,
					body: response.data.body,
					cc: response.data.cc,
					bcc: response.data.bcc,
					attachments: response.data.selectedAttachments,
				});
			}
			setIsLoading(false);
		} catch (err) {
			setError('Failed to load email preview');
			console.error('Error fetching email preview:', err);
			setIsLoading(false);
		}
	};

	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
	};

	const handleSendEmail = async () => {
		try {
			setIsSending(true);
			setError('');
			setSuccess('');
			const validAttachments = formData.attachments;
			const formDataToSend = new FormData();
			const emailDataPayload = {
				model, id: recordId,
				to: formData.to,
				subject: formData.subject,
				body: formData.body,  // This will now have the updated HTML
				cc: formData.cc,
				bcc: formData.bcc,
				attachments: validAttachments,
			};
			formDataToSend.append('emailData', JSON.stringify(emailDataPayload));
			validAttachments?.filter((i) => i?.originalType === 'local').forEach((attachment) => {
				formDataToSend.append('emailFiles', attachment.file, attachment.name);
			});
			const response = await postAPI('email', formDataToSend, '/send/custom');
			if (response.success) {
				fnSuccess(emailDataPayload);
				setSuccess('Email envoyé avec succès !');
				setIsOpen(false);
			}
		} catch (err) {
			setError(err.message);
			console.error('Error sending email:', err);
		} finally {
			setIsSending(false);
		}
	};

	const handleClose = () => {
		setIsOpen(false);
		setEmailData(null);
		setFormData({ to: '', subject: '', body: '', cc: [], bcc: [], attachments: [] });
		setError('');
		setSuccess('');
	};

	return (
		<Modal show={isOpen} onHide={handleClose} size='lg'>
			<Modal.Header closeButton>
				<Modal.Title style={{ fontSize: '1rem', fontWeight: 700 }}>
					<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' style={{ marginRight: 8, verticalAlign: -2 }} aria-hidden='true'>
						<path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z' /><polyline points='22,6 12,13 2,6' />
					</svg>
					Envoyer un e-mail
				</Modal.Title>
			</Modal.Header>

			<Modal.Body style={{ padding: '20px 24px' }}>
				{isLoading ? (
					<div style={{ textAlign: 'center', padding: '40px 0' }}>
						<Spinner animation='border' role='status' style={{ color: '#185FA5' }} />
						<div style={{ marginTop: 12, fontSize: '0.88rem', color: '#64748b' }}>Préparation email…</div>
					</div>
				) : (
					<>
						<Form>
							{/* To */}
							<Row>
								<Form.Group className='mb-3'>
									<Form.Label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>Destinataire *</Form.Label>
									<Form.Control
										type='email'
										name='to'
										value={formData.to}
										onChange={handleInputChange}
										required
										style={{ fontSize: '0.88rem' }}
									/>
									<Form.Text className='text-muted' style={{ fontSize: '0.75rem' }}>
										mail1@gmail.com, mail2@gmail.com
									</Form.Text>
								</Form.Group>
							</Row>

							{/* Subject */}
							<Row>
								<Form.Group className='mb-3'>
									<Form.Label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>Sujet *</Form.Label>
									<Form.Control
										type='text'
										name='subject'
										value={formData.subject}
										onChange={handleInputChange}
										required
										style={{ fontSize: '0.88rem' }}
									/>
								</Form.Group>
							</Row>

							{/* Body editor */}
							{!isMobile && (
								<Form.Group className='mb-3'>
									<Form.Label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: 8, display: 'block' }}>
										Corps du message *
									</Form.Label>
									<HtmlBlockEditor
										body={formData.body || ''}
										onChange={(newBody) =>
											setFormData((prev) => ({ ...prev, body: newBody }))
										}
									/>
								</Form.Group>
							)}

							{/* Attachments */}
							<Form.Group className='mb-1'>
								<Form.Label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>Pièces jointes</Form.Label>
								{isLoadingAttachments ? (
									<div style={{ textAlign: 'center', padding: '20px 0' }}>
										<Spinner animation='border' size='sm' style={{ color: '#185FA5' }} />
										<span style={{ marginLeft: 8, fontSize: '0.85rem', color: '#64748b' }}>Préparation pièces jointes…</span>
									</div>
								) : (
									<div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
										{/* Attachment list */}
										{formData.attachments.length > 0 && (
											<div style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}>
												{formData.attachments.map((attachment, index) => (
													<div
														key={index}
														style={{
															display: 'flex', alignItems: 'center', gap: 8,
															padding: '6px 0',
															borderBottom: index < formData.attachments.length - 1 ? '1px solid #f8fafc' : 'none',
														}}
													>
														<svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='#E24B4A' strokeWidth='2' aria-hidden='true'>
															<path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' /><polyline points='14,2 14,8 20,8' />
														</svg>
														<a
															className='text-decoration-none'
															href={attachment?.originalType === 'local' ? attachment.url : `/coreService/${attachment.url}`}
															target='_blank'
															rel='noopener noreferrer'
															style={{ fontSize: '0.83rem', color: '#185FA5', fontWeight: 500, flex: 1 }}
														>
															{attachment.name}
														</a>
														<span style={{ fontSize: '0.68rem', background: '#FCEBEB', color: '#A32D2D', borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}>PDF</span>
														<button
															type='button'
															onClick={() => {
																setFormData((prev) => ({
																	...prev,
																	attachments: prev.attachments.filter((_, i) => i !== index),
																}));
															}}
															style={{
																background: 'none', border: 'none', cursor: 'pointer',
																color: '#94a3b8', padding: '2px 4px', borderRadius: 4,
																fontSize: '1rem', lineHeight: 1, fontWeight: 700,
															}}
															title='Supprimer'
														>×</button>
													</div>
												))}
											</div>
										)}

										{/* Add attachment */}
										<div style={{ padding: '10px 12px', background: '#f8fafc' }}>
											<button
												type='button'
												onClick={() => document.getElementById('attachment-file-input').click()}
												style={{
													background: 'none', border: '1px dashed #cbd5e1', borderRadius: 6,
													color: '#64748b', cursor: 'pointer', padding: '6px 14px',
													fontSize: '0.82rem', fontWeight: 500, display: 'flex',
													alignItems: 'center', gap: 6, transition: 'all 0.15s',
												}}
												onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#185FA5'; e.currentTarget.style.color = '#185FA5'; }}
												onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#64748b'; }}
											>
												<svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' aria-hidden='true'>
													<path d='M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48' />
												</svg>
												Joindre un fichier
											</button>
											<input
												id='attachment-file-input'
												type='file'
												multiple
												style={{ display: 'none' }}
												onChange={(e) => {
													const newLocalAttachments = Array.from(e.target.files).map((file) => ({
														name: file.name,
														originalType: 'local',
														file: file,
														url: URL.createObjectURL(file),
													}));
													setFormData((prev) => ({
														...prev,
														attachments: [...prev.attachments, ...newLocalAttachments],
													}));
													e.target.value = '';
												}}
											/>
										</div>
									</div>
								)}
							</Form.Group>
						</Form>
					</>
				)}

				{error && (
					<Alert variant='danger' onClose={() => setError('')} dismissible className='mt-3' style={{ fontSize: '0.85rem' }}>
						{error}
					</Alert>
				)}
				{success && (
					<Alert variant='success' onClose={() => setSuccess('')} dismissible className='mt-3' style={{ fontSize: '0.85rem' }}>
						{success}
					</Alert>
				)}
			</Modal.Body>

			<Modal.Footer style={{ borderTop: '1px solid #f1f5f9', padding: '12px 24px' }}>
				<Button
					variant='light'
					onClick={handleClose}
					style={{ fontSize: '0.85rem', fontWeight: 600, borderRadius: 7 }}
				>
					Annuler
				</Button>
				<Button
					variant='primary'
					onClick={handleSendEmail}
					disabled={isSending || !formData.to || !formData.subject || !formData.body}
					style={{ fontSize: '0.85rem', fontWeight: 600, borderRadius: 7, background: '#185FA5', border: 'none', padding: '8px 20px' }}
				>
					{isSending ? (
						<>
							<Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' className='me-2' />
							Envoi…
						</>
					) : (
						<>
							<svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' style={{ marginRight: 6, verticalAlign: -1 }} aria-hidden='true'>
								<line x1='22' y1='2' x2='11' y2='13' /><polygon points='22 2 15 22 11 13 2 9 22 2' />
							</svg>
							Envoyer
						</>
					)}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default EmailSendPopup;